import type { Image, Link, RichText } from '@eneco/cms-types';
import { asRichText } from '@eneco/cms-types';
import { getCombinedLinkFieldValueHref } from '@eneco/common/link';

import type { JssField, JssImageFieldValue } from './jss-types';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isBlank = (value: unknown): boolean => typeof value !== 'string' || value.trim().length === 0;

/**
 * Every key a Sitecore link value carries: what the layout service sends for a General Link field
 * (`href`, `text`, `linktype`, `anchor`, `querystring`, `target`, `title`, `class`, `id`) and `url`,
 * which some fields add beside `href`.
 */
const LINK_VALUE_KEYS: ReadonlySet<string> = new Set([
  'href',
  'text',
  'linktype',
  'anchor',
  'querystring',
  'target',
  'title',
  'class',
  'id',
  'url',
]);

/**
 * A Sitecore link value, told apart by its shape: an `href` and nothing beside it but the keys of a
 * link value. `linktype` alone would miss an empty link, which arrives as a bare `{ href: '' }`. And
 * `href` alone would take in the `<link>` tags `MetaData` holds in `jss-meta`, which carry `rel`. A
 * link already converted carries `linkType` (and `rel` with a new tab), so it is never converted twice.
 */
const isLinkValue = (value: Record<string, unknown>): boolean =>
  typeof value.href === 'string' && Object.keys(value).every(key => LINK_VALUE_KEYS.has(key));

/**
 * A Sitecore link value in the shape both adapters hand components a link in (`Link`). Additive, so
 * the live Sitecore lane, where `SitecoreCmsProvider` and the bridged components read content through
 * this adapter, renders as before:
 *
 * - `href` is the whole URL: the query string and anchor are added the way `@eneco/next`'s `Link`
 *   always added them (`getCombinedLinkFieldValueHref`), and dropped as separate keys, so a component
 *   that hands this to `Link` as `linkValue` does not get them twice.
 * - `linkType` joins Sitecore's own `linktype`, and a link set to open a new tab (`_blank`) gets
 *   `rel="noopener noreferrer"`.
 * - Everything else stays as Sitecore sends it: `text` and `title` (an empty string included, which a
 *   component may fall back on), `target` (its raw `|Custom` included), `class`, `id` and `url`.
 */
const toSharedLink = (value: Record<string, unknown>): Link & Record<string, unknown> => {
  const { anchor, querystring, ...sitecoreOwn } = value;
  return {
    ...sitecoreOwn,
    href: getCombinedLinkFieldValueHref({
      href: value.href as string,
      ...(typeof anchor === 'string' ? { anchor } : {}),
      ...(typeof querystring === 'string' ? { querystring } : {}),
    }),
    ...(value.target === '_blank' ? { rel: 'noopener noreferrer' } : {}),
    ...(isBlank(value.linktype) ? {} : { linkType: value.linktype as string }),
  };
};

/**
 * What a JSS field may carry beside `value`.
 *
 * `value` alone is not enough to identify a field, because plenty of authored
 * content has a `value` property of its own. A droplist's options are
 * `{ name, value, label }` and a flow's navigation steps are the same, so
 * matching on `value` collapsed each option to its own value string and threw
 * the rest away: a navigation rendered four unlabelled steps and a radio group
 * rendered choices with no text, both silently.
 *
 * The distinction is that a field's other keys are metadata about the field
 * while an option's are content. `metadata` rides along in the Experience
 * Editor and `linktype` sits on the route-level path fields.
 *
 * Editor markup needs three names, because JSS does not have one name for it:
 * text, image and date fields carry `editable`, while a link carries
 * `editableFirstPart` and `editableLastPart`, its markup wrapping the link's
 * children. Listing only `editable` meant every link failed this test in the
 * Experience Editor and was left wrapped, so components read `href` off the
 * wrapper and found nothing. Outside the editor those keys are absent, which
 * is why it rendered correctly everywhere else.
 *
 * This is the whole set that `@sitecore-jss` 22.12.3 declares. A JSS upgrade
 * that adds a fourth name would leave that field wrapped, and silently, so
 * check this list when the dependency moves.
 *
 * A shape carrying any other key is content.
 */
const FIELD_KEYS = new Set(['value', 'editable', 'editableFirstPart', 'editableLastPart', 'metadata', 'linktype']);

export const isField = (value: unknown): value is JssField =>
  isObject(value) && 'value' in value && Object.keys(value).every(key => FIELD_KEYS.has(key));

/**
 * An item referenced by a multilist, droplink or droptree. JSS serialises all
 * three the same way, as `{ id?, url?, name?, displayName?, fields: { ... } }`:
 * the item's own content lives one level down, under `fields`. A multilist
 * holds many such references and a droplink one, but the shape is identical,
 * and the referenced item need not sit under the datasource.
 *
 * That key is structure rather than a field wrapper, so `isField` does not match
 * it and the generic object branch would keep it. Sanity has no equivalent, so
 * leaving it would force every component with a list either to unwrap it by hand
 * or to declare the wrapper in its content type, which puts a Sitecore transport
 * shape in the layer that exists to be free of one.
 */
export const isItem = (value: unknown): value is Record<string, unknown> & { fields: Record<string, unknown> } =>
  isObject(value) && 'fields' in value && !('value' in value) && isObject(value.fields);

/**
 * Recursively unwrap `{ value: ... }` JSS field shapes into plain values.
 * This is the core trick that lets components write `content.title`
 * instead of `f('title.value')`.
 *
 * A link value comes out in the shape both adapters hand components a link in (see
 * `toSharedLink`), wherever it sits: a field's value, an item in a list, a member of a
 * custom field such as a radio option's `redirectLink`.
 */
export const unwrapFields = (fields: Record<string, unknown> | undefined): Record<string, unknown> => {
  if (!fields) return {};
  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(fields)) {
    out[key] = unwrap(raw);
  }
  return out;
};

const unwrap = (raw: unknown): unknown => {
  if (Array.isArray(raw)) return raw.map(unwrap);
  if (isField(raw)) {
    // Recurse so nested object/array shapes inside `value` are also unwrapped.
    return unwrap(raw.value);
  }
  if (isItem(raw)) {
    // Lift the item's own content up a level. The remaining keys (`id`, `name`,
    // `url`, `displayName`) are kept rather than dropped, because components use
    // them for stable list keys. Fields win a name clash: an authored field is
    // content, the siblings are item metadata.
    const { fields, ...rest } = raw;
    return unwrapFields({ ...rest, ...fields });
  }
  if (isObject(raw)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(raw)) out[k] = k === 'options' ? withOptionNames(unwrap(v)) : unwrap(v);
    return isLinkValue(out) ? toSharedLink(out) : out;
  }
  return raw;
};

/**
 * A select field's options in the shape both adapters hand them over: each with its code under `name`.
 * Sitecore's `InputSelectField` keeps the code in `value` (`{ label, value }`), the Sanity migration wrote it
 * to `name`, and the generated content types follow Sanity. Additive: the option keeps its `value`, and an
 * option that already has a `name`, such as a radio group's, is left as it is.
 */
const withOptionNames = (options: unknown): unknown =>
  Array.isArray(options)
    ? options.map(option =>
        isObject(option) && typeof option.value === 'string' && !('name' in option)
          ? { ...option, name: option.value }
          : option,
      )
    : options;

interface JssFormat {
  format: string;
  src: string;
  width?: string;
  height?: string;
}

const breakpointForFormat = (format: string): string | undefined => {
  switch (format) {
    case 'small':
      return 'sm';
    case 'medium':
      return 'md';
    case 'large':
      return 'lg';
    case 'extraLarge':
      return 'xl';
    case 'widescreen':
      return 'xxl';
    default:
      return undefined;
  }
};

const stripPx = (value: string | undefined): number | undefined => {
  if (!value) return undefined;
  const parsed = Number.parseInt(value.replace('px', ''), 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * A FocalPointImage's sizes as one image, its `src` and size from the size `mapImage` (`@sitecore/common`) shows
 * for that serialisation: `default` for the `formats` list, `small` for the sizes kept under `fields`, each falling
 * back on the other. Every component that still reads Sitecore directly goes through `mapImage`, so a component that
 * moves to this adapter keeps showing the same image.
 */
const fromFormats = (alt: string | undefined, formats: JssFormat[], shown: 'default' | 'small'): Image | undefined => {
  const valid = formats.filter(f => typeof f?.src === 'string' && f.src.length > 0);
  if (valid.length === 0) return undefined;
  const fallback = shown === 'default' ? 'small' : 'default';
  const primary = valid.find(f => f.format === shown) ?? valid.find(f => f.format === fallback) ?? valid[0];
  const sources: Record<string, string> = {};
  for (const f of valid) {
    const bp = breakpointForFormat(f.format);
    if (bp) sources[bp] = f.src;
  }
  const srcSet = valid.map(f => (f.width ? `${f.src} ${f.width.replace('px', 'w')}` : f.src)).join(', \n');
  const width = stripPx(primary.width);
  const height = stripPx(primary.height);
  const aspectRatio = width && height ? `${width}/${height}` : undefined;
  return {
    src: primary.src,
    ...(alt !== undefined ? { alt } : {}),
    ...(width !== undefined ? { width } : {}),
    ...(height !== undefined ? { height } : {}),
    ...(Object.keys(sources).length > 0 ? { sources } : {}),
    ...(srcSet ? { srcSet } : {}),
    ...(aspectRatio ? { aspectRatio } : {}),
  };
};

/**
 * The other FocalPointImage serialisation, which keeps its sizes under `fields`. `unwrap` takes that for a
 * referenced item and lifts the sizes up beside `alt` (see `isItem`), so the image arrives as
 * `{ alt, default: { src, width, height }, small: { ... } }`: the same image as the `formats` list, each
 * size named by its key rather than by `format`.
 */
const liftedSizes = (value: Record<string, unknown>): JssFormat[] =>
  Object.entries(value).flatMap(([format, size]) =>
    isObject(size) && typeof size.src === 'string'
      ? [
          {
            format,
            src: size.src,
            ...(typeof size.width === 'string' ? { width: size.width } : {}),
            ...(typeof size.height === 'string' ? { height: size.height } : {}),
          },
        ]
      : [],
  );

export const toImage = (raw: unknown): Image | undefined => {
  if (raw === null || raw === undefined) return undefined;
  const value = isField(raw) ? raw.value : raw;
  if (!isObject(value)) return undefined;
  const alt = typeof (value as { alt?: unknown }).alt === 'string' ? (value as { alt: string }).alt : undefined;
  // FocalPointImage shape: { alt, formats: [{format, src, width, height}, ...] }
  if (Array.isArray((value as { formats?: unknown }).formats)) {
    return fromFormats(alt, (value as { formats: JssFormat[] }).formats, 'default');
  }
  const img = value as JssImageFieldValue;
  if (typeof img.src !== 'string' || img.src.length === 0) {
    const sizes = liftedSizes(value);
    return sizes.length > 0 ? fromFormats(alt, sizes, 'small') : undefined;
  }
  const width = typeof img.width === 'string' ? Number(img.width) || undefined : img.width;
  const height = typeof img.height === 'string' ? Number(img.height) || undefined : img.height;
  return {
    src: img.src,
    ...(img.alt !== undefined ? { alt: img.alt } : {}),
    ...(width !== undefined ? { width } : {}),
    ...(height !== undefined ? { height } : {}),
  };
};

/**
 * Sitecore stores rich text as raw HTML strings. We keep the markup as-is
 * (consumers are responsible for safe rendering) and brand it as `RichText`.
 */
export const toRichText = (raw: unknown): RichText | undefined => {
  if (raw === null || raw === undefined) return undefined;
  const value = isField(raw) ? raw.value : raw;
  if (typeof value !== 'string' || value.length === 0) return undefined;
  return asRichText(value);
};
